import { useCallback, useRef } from 'react';
import { api } from '../../../api/Middleware';
import { API_URL } from '../../../api/Urls';

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
// App ID = project number (parte numérica do Client ID, antes do "-")
const GOOGLE_APP_ID = GOOGLE_CLIENT_ID ? GOOGLE_CLIENT_ID.split('-')[0] : '';

/**
 * Carrega o script do Google API (gapi) sob demanda.
 */
let gapiLoadPromise = null;
function loadGapiScript() {
  if (gapiLoadPromise) return gapiLoadPromise;

  gapiLoadPromise = new Promise((resolve, reject) => {
    if (window.gapi) {
      resolve(window.gapi);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.onload = () => resolve(window.gapi);
    script.onerror = () => {
      gapiLoadPromise = null;
      reject(new Error('Falha ao carregar Google API'));
    };
    document.head.appendChild(script);
  });

  return gapiLoadPromise;
}

/**
 * Carrega a lib do Picker dentro do gapi.
 */
function loadPickerLib() {
  return new Promise((resolve, reject) => {
    window.gapi.load('picker', {
      callback: resolve,
      onerror: () => reject(new Error('Falha ao carregar Google Picker')),
    });
  });
}

// MIME types para RAG (Drive files)
export const DRIVE_RAG_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'text/plain',
  'application/vnd.google-apps.document',
  'application/vnd.google-apps.spreadsheet',
].join(',');

// MIME type para planilhas apenas
export const SHEETS_MIME_TYPE = 'application/vnd.google-apps.spreadsheet';

/**
 * Hook para abrir o Google Picker.
 *
 * @param {Object} options
 * @param {string} options.agentId - UUID do agente
 * @param {string} options.integrationId - UUID da integração Google ativa
 * @param {string} options.integrationType - Tipo da integração (google_drive, google_sheets, etc.)
 * @param {Function} options.onSelect - Callback com array de arquivos selecionados
 * @param {Function} [options.onError] - Callback de erro
 * @param {string} [options.mimeTypes] - MIME types separados por vírgula (default: DRIVE_RAG_MIME_TYPES)
 * @param {boolean} [options.multiSelect] - Permitir múltipla seleção (default: false)
 */
export function useGooglePicker({
  agentId,
  integrationId,
  integrationType = 'google_workspace',
  onSelect,
  onError,
  mimeTypes = DRIVE_RAG_MIME_TYPES,
  multiSelect = false,
}) {
  const loadingRef = useRef(false);

  const openPicker = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      // 1. Obter access_token válido do backend
      const { data } = await api.get(`${API_URL}agents_flow/google/picker_token/`, {
        params: {
          agent_flow_id: agentId,
          integration_type: integrationType,
        },
      });

      if (!data.access_token) {
        throw new Error('Token não disponível. Reconecte a integração Google.');
      }

      // 2. Carregar gapi + picker lib
      await loadGapiScript();
      await loadPickerLib();

      // 3. Montar view
      const view = new window.google.picker.DocsView()
        .setMimeTypes(mimeTypes)
        .setMode(window.google.picker.DocsViewMode.LIST)
        .setIncludeFolders(false);

      // 4. Montar picker (setAppId é obrigatório para drive.file funcionar)
      const builder = new window.google.picker.PickerBuilder()
        .setAppId(GOOGLE_APP_ID)
        .setOAuthToken(data.access_token)
        .setCallback((result) => {
          if (result.action === window.google.picker.Action.PICKED) {
            const files = result.docs.map((doc) => ({
              id: doc.id,
              name: doc.name,
              mime_type: doc.mimeType,
              size: doc.sizeBytes || 0,
              url: doc.url,
              icon_url: doc.iconUrl,
            }));
            onSelect(files);
          }
        })
        .addView(view)
        .setTitle('Selecionar arquivos')
        .setLocale('pt-BR');

      if (GOOGLE_API_KEY) {
        builder.setDeveloperKey(GOOGLE_API_KEY);
      }

      if (multiSelect) {
        builder.enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED);
      }

      builder.build().setVisible(true);

      // Garantir que o Picker fique acima de modais/offcanvas (z-index 1045+)
      requestAnimationFrame(() => {
        const pickerElements = document.querySelectorAll('.picker-dialog-bg, .picker-dialog');
        pickerElements.forEach((el) => {
          el.style.zIndex = '2000';
        });
      });
    } catch (err) {
      console.error('[GooglePicker] Erro:', err);
      if (onError) {
        onError(err.message || 'Erro ao abrir seletor de arquivos');
      }
    } finally {
      loadingRef.current = false;
    }
  }, [agentId, integrationId, integrationType, mimeTypes, multiSelect, onSelect, onError]);

  return { openPicker };
}

export default useGooglePicker;
